import { Actor, Animation, clamp, Color, Engine, Sound, SpriteSheet, vec, Vector } from 'excalibur';
import { resources } from '../../../Resources';
import { controller } from '../../../shared/controls/controller';
import { tintAnimation } from '../../../shared/utilities/tintAnimation';
import { ExplosionParticles } from './ExplosionParticles';
import { Missile } from './Missile';

export class Helicopter extends Actor {
    private _vx = 0;
    private _vy = 0;
    private _rotorPower = 0;
    private _rotorSpeed = 0;
    private _lookingRight = true;
    private _maxTilt = 1;
    private _maxLift = 1700;
    private _backwardThrustScale = 0.5;
    private _tiltResponsiveness = 8;

    private _fireCooldown = 0;
    private readonly _fireInterval = 0.18; // seconds
    private readonly _missileSpeed = 900;  // px/s
    private _visible = true;


    private _homePos: Vector;

    constructor(
        position: Vector,
        private _controller: ReturnType<typeof controller>,
        private _tintColor: Color,
    ) {
        super({
            pos: position,
            width: 60,
            height: 92
            , // Match sprite height for correct collision
            anchor: vec(0.5, 0.5),
        });
        this._homePos = position.clone();
    }

    private _health = 20;
    public damage(amount: number) {
        this._health -= amount;
        if (this._health <= 0) {
            this._visible = false;
            // Spawn explosion effect at current position
            if (this.scene) {
                this.scene.add(new ExplosionParticles(this.pos.x, this.pos.y));
                const explodeSound = new Sound(resources.games.helicopters.actors.helicopter.sfxExplode.path);
                explodeSound.volume = 0.4 + Math.random() * 0.1;
                explodeSound.playbackRate = 0.9 + Math.random() * 0.2;
                explodeSound.play();
            }
            // Place off-screen
            this.pos = vec(-1000, -1000);
        }
    }


    private spriteSheetRight = resources.games.helicopters.actors.helicopter.spritesheetHueyRight300x2881x3;
    private spriteSheetLeft = resources.games.helicopters.actors.helicopter.spritesheetHueyLeft300x2881x3;

    private sheetRight!: SpriteSheet;
    private sheetLeft!: SpriteSheet;

    private animRight!: Animation;
    private animLeft!: Animation;

    private pilot?: Actor;
    private copilot?: Actor;
    private hull?: Actor;

    private static readonly SEAT_PILOT = vec(55, 8);
    private static readonly SEAT_COPILOT = vec(51, 7);

    private static pilotSheet: SpriteSheet;
    private static pilotAnim: Animation;

    private static copilotSheet: SpriteSheet;
    private static copilotAnim: Animation;

    override onInitialize(engine: Engine): void {
        // Handle collision with other helicopters
        if (!this._controller) throw new Error(`No gamepad found for controller index ${this._controller}`);

        // Create new SpriteSheet and Animation instances for this helicopter
        this.sheetRight = SpriteSheet.fromImageSource({
            image: this.spriteSheetRight,
            grid: { rows: 3, columns: 1, spriteWidth: 300, spriteHeight: 96 },
        });
        this.sheetLeft = SpriteSheet.fromImageSource({
            image: this.spriteSheetLeft,
            grid: { rows: 3, columns: 1, spriteWidth: 300, spriteHeight: 96 },
        });
        this.animRight = Animation.fromSpriteSheet(this.sheetRight, [0, 1, 2], 25);
        this.animLeft = Animation.fromSpriteSheet(this.sheetLeft, [0, 1, 2], 25);

        if (!Helicopter.pilotSheet) {
            Helicopter.pilotSheet = SpriteSheet.fromImageSource({
                image: resources.games.helicopters.actors.helicopter.spritesheetPilot32x642x1,
                grid: { rows: 1, columns: 2, spriteWidth: 32, spriteHeight: 32 },
            });
            Helicopter.pilotAnim = Animation.fromSpriteSheet(Helicopter.pilotSheet, [0, 1], 200);
        }

        if (!Helicopter.copilotSheet) {
            Helicopter.copilotSheet = SpriteSheet.fromImageSource({
                image: resources.games.helicopters.actors.helicopter.spritesheetPilot32x642x1,
                grid: { rows: 1, columns: 2, spriteWidth: 32, spriteHeight: 32 },
            });
            Helicopter.copilotAnim = Animation.fromSpriteSheet(Helicopter.copilotSheet, [0, 1], 210);
        }

        // Create child actors (pilots) first = behind
        this.pilot = new Actor({ pos: Helicopter.SEAT_PILOT.clone(), anchor: vec(0.5, 0.5) });
        this.pilot.scale = vec(0.4, 0.4);
        this.pilot.graphics.use(Helicopter.pilotAnim);
        this.addChild(this.pilot);

        this.copilot = new Actor({ pos: Helicopter.SEAT_COPILOT.clone(), anchor: vec(0.5, 0.5) });
        this.copilot.scale = vec(0.4, 0.4);
        this.copilot.graphics.use(Helicopter.copilotAnim);
        this.addChild(this.copilot);


        // Hull LAST = in front
        this.hull = new Actor({ pos: vec(0, 0), anchor: vec(0.5, 0.5) });
        this.applyHullFacing(); // pick initial anim
        this.addChild(this.hull);

        if (this._tintColor) {
            tintAnimation(this.animRight, this._tintColor);
            tintAnimation(this.animLeft, this._tintColor);
        }

        const rotorSound = new Sound(resources.games.helicopters.actors.helicopter.sfxHuey.path);
        rotorSound.loop = true;
        rotorSound.volume = 0.16 + Math.random() * 0.06;
        rotorSound.playbackRate = 0.98 + Math.random() * 0.05;
        rotorSound.play({ volume: 0.18 + Math.random() * 0.05 });
    }

    override onPreUpdate(engine: Engine, elapsedMs: number): void {
        let terrainSegments: Array<{ a: { x: number; y: number }; b: { x: number; y: number } }> = [];
        if (engine.currentScene && (engine.currentScene as any).terrain && (engine.currentScene as any).terrain.segments) {
            terrainSegments = (engine.currentScene as any).terrain.segments;
        }

        const deltaTime = elapsedMs / 1000;
        this.handleControlsAndPhysics(deltaTime, terrainSegments);

        this._fireCooldown = Math.max(0, this._fireCooldown - deltaTime);
        this.fire();
    }

    public handleControlsAndPhysics(
        deltaTime: number,
        terrainSegments?: Array<{ a: { x: number; y: number }; b: { x: number; y: number } }>,
    ) {
        this.reset();

        if (!this._visible || !this._controller) return;

        this.updateDirection();
        this.updatePower();
        this.updateRotation(deltaTime);
        this.updatePhysics(deltaTime, terrainSegments);
    }

    private updateDirection() {
        if (this._controller?.yOrTriangle.pressed) {
            this._lookingRight = !this._lookingRight;
            this.applyHullFacing();
        }
    }

    private applyHullFacing() {
        if (!this.hull) return;
        this.hull.graphics.use(this._lookingRight ? this.animRight : this.animLeft);
    }

    private fire() {
        if (!this.scene || !this._controller?.xOrSquare.holding) return;

        if (this._fireCooldown > 0) return;
        this._fireCooldown = this._fireInterval;

        const forward = vec(Math.cos(this._pitch), Math.sin(this._pitch));
        const heliHalf = (this.collider?.bounds?.width ?? this.width) * 0.5;

        const missileHalfLen = 10 * 0.5;
        const margin = 6;

        const perp = vec(-forward.y, forward.x).scale(2);

        let spawnPos = this.pos
            .add((this._lookingRight ? forward : forward.negate()).scale(heliHalf + missileHalfLen + margin))
            .add(perp);

        const missilePitch = this._lookingRight ? this._pitch : (this._pitch + Math.PI);
        const missile = new Missile(spawnPos.x, spawnPos.y, missilePitch, this._missileSpeed);
        this.scene.add(missile);
    }

    private updatePower() {
        this._rotorPower = this._controller?.rightTrigger.holding ?? 0;
    }

    private reset() {
        if (!this._controller?.back.pressed) return;
        this.pos = this._homePos.clone();
        this._vx = 0;
        this._vy = 0;
        this._health = 20;
        this._visible = true;
    }

    private _pitch = 0;

    private updateRotation(dt: number) {
        const x = this._controller?.leftStick.x ?? 0;

        const targetPitch = clamp(x, -1, 1) * this._maxTilt;

        const alpha = 1 - Math.exp(-this._tiltResponsiveness * dt);
        this._pitch += (targetPitch - this._pitch) * alpha;

        this._pitch = clamp(this._pitch, -this._maxTilt, this._maxTilt);

        // No sprite flipping now; only affects physics/aim convention
        this.rotation = this._pitch;
    }

    public updatePhysics(dt: number, terrainSegments?: Array<{ a: { x: number; y: number }; b: { x: number; y: number } }>) {
        this._rotorSpeed += (this._rotorPower - this._rotorSpeed) * 0.1;
        const lift = this._rotorSpeed * this._maxLift * Math.cos(this.rotation);

        let forwardAccel = (this._rotorSpeed * this._maxLift * Math.sin(this.rotation));

        const isBackward = this._lookingRight ? forwardAccel < 0 : forwardAccel > 0;
        if (isBackward) forwardAccel *= this._backwardThrustScale;

        const gravity = 800;

        this._vy += (-lift + gravity) * dt;
        this._vx += forwardAccel * dt;


        this._vx *= 0.99;
        this._vy *= 0.99;
        this.pos.x += this._vx * dt;
        this.pos.y += this._vy * dt;

        // Prevent helicopter from leaving the screen
        const halfW = this.width / 2;
        const halfH = this.height / 2;
        if (this.scene && (this.scene.engine || (this.scene as any)._engine)) {
            // Excalibur 0.27+ uses scene.engine, older uses _engine
            const engine = (this.scene.engine || (this.scene as any)._engine);
            const maxX = engine.drawWidth - halfW;
            const maxY = engine.drawHeight - halfH;
            this.pos.x = clamp(this.pos.x, halfW, maxX);
            this.pos.y = clamp(this.pos.y, halfH, maxY);
        }

        if (terrainSegments && terrainSegments.length > 0) {
            for (const seg of terrainSegments) {
                const minX = Math.min(seg.a.x, seg.b.x);
                const maxX = Math.max(seg.a.x, seg.b.x);
                if (this.pos.x >= minX && this.pos.x <= maxX) {
                    const t = (this.pos.x - seg.a.x) / (seg.b.x - seg.a.x);
                    const terrainY = seg.a.y + t * (seg.b.y - seg.a.y);
                    // Calculate bottom of helicopter (anchor is 0.5,0.5)
                    const heliBottom = this.pos.y + this.height / 2;
                    if (heliBottom > terrainY) {
                        this.pos.y = terrainY - this.height / 2;
                        this._vy = 0;
                    }
                }
            }
        }
    }
}
